import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useDecryptCpr } from '@/hooks/usePatients';
import { formatCprForPrint, printHtml } from '@/lib/referral/referralPrint';
import {
  ggopHeading,
  ggopPrintHtml,
  ggopScreenHtml,
  loadGgopStylesheet,
  renderGgop,
  withGgopCpr,
  type GgopRendered,
} from '@/lib/ggop/ggopView';

// T2.2 / T2.4 (GGOP-M 2.6, 2.8, 3.1–3.9; GGOP-S 3.5.12): one genoptræningsplan shown with
// MedCom's own stylesheet, and printed with the plan type heading plus the patient's
// name and CPR on every page. The XML arrives with the CPR masked; the CPR is read
// via decrypt-cpr (audited) only when the plan is coupled to a patient.

export function GgopViewer({
  xml,
  planType,
  patientId,
  patientName,
  canRevealCpr = true,
}: {
  /** The plan as communicate-ggop returns it (CPR replaced by the marker). */
  xml: string;
  planType: string | null;
  patientId: string | null;
  patientName: string | null;
  canRevealCpr?: boolean;
}) {
  const { t } = useTranslation();
  const decrypt = useDecryptCpr();
  const [xsl, setXsl] = useState<string | null>(null);
  const [cpr, setCpr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const heading = ggopHeading(planType);
  const hidden = t('patients.ggop.cpr_hidden', 'CPR skjult');

  useEffect(() => {
    let cancelled = false;
    loadGgopStylesheet().then(
      (value) => !cancelled && setXsl(value),
      (err: unknown) => !cancelled && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCpr(null);
    if (!patientId || !canRevealCpr) return;
    let cancelled = false;
    decrypt.mutateAsync({ patient_id: patientId, source: 'ggop-view', purpose: 'ggop' }).then(
      (result) => !cancelled && setCpr(result.cpr),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
    // mutateAsync is stable; the mutation object is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, canRevealCpr]);

  const { rendered, renderError } = useMemo<{ rendered: GgopRendered | null; renderError: string | null }>(() => {
    if (!xsl) return { rendered: null, renderError: null };
    try {
      return { rendered: renderGgop(withGgopCpr(xml, cpr, hidden), xsl), renderError: null };
    } catch (err) {
      return { rendered: null, renderError: err instanceof Error ? err.message : String(err) };
    }
  }, [xml, xsl, cpr, hidden]);
  const shownError = error ?? renderError;

  function onPrint() {
    if (!rendered) return;
    printHtml(
      ggopPrintHtml({
        rendered,
        heading,
        patientName: patientName ?? '',
        cprText: cpr ? formatCprForPrint(cpr) : hidden,
        labels: {
          cpr: 'CPR',
          page: t('patients.ggop.print_page', 'Side'),
          of: t('patients.ggop.print_of', 'af'),
          printedAt: `${t('patients.ggop.printed_at', 'Udskrevet')} ${new Date().toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'short', timeStyle: 'short' })}`,
        },
      }),
    );
  }

  return (
    <div className="space-y-2" data-testid="ggop-viewer">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-base font-semibold" data-testid="ggop-heading">{heading}</h4>
        <Button type="button" size="sm" variant="secondary" onClick={onPrint} disabled={!rendered}>
          <Printer className="mr-1.5 h-4 w-4" />
          {t('patients.ggop.print', 'Udskriv')}
        </Button>
      </div>
      {shownError && <p role="alert" className="text-sm text-ds-red-ink">{shownError}</p>}
      {!rendered && !shownError && <p className="text-sm text-ds-muted">{t('patients.ggop.rendering', 'Viser genoptræningsplanen…')}</p>}
      {rendered && (
        <iframe
          title={heading}
          sandbox=""
          srcDoc={ggopScreenHtml(rendered)}
          className="h-[70vh] w-full rounded-md border border-ds-line bg-ds-card"
        />
      )}
    </div>
  );
}
